import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faWifi, faWifiFair, faWifiWeak } from '@fortawesome/pro-duotone-svg-icons';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import {
  ConnectionStatsService,
  LOSS_FAIR_PCT,
  LOSS_POOR_PCT,
  RTT_FAIR_MS,
  RTT_POOR_MS,
} from '../../services/connection-stats.service';
import {
  healthStateLabel,
  StreamHealthService,
  StreamKind,
} from '../../services/stream-health.service';
import { UserStore } from '../../../state/user.repository';
import { StreamStatsDialogService } from '../../../features/video/stream-stats/stream-stats-dialog.service';
import { StatsGraph } from '../../../shared/components/stats-graph/stats-graph';

const QUALITY = {
  good: { icon: faWifi, iconClass: 'duotone-success', label: _('Good') },
  fair: { icon: faWifiFair, iconClass: 'duotone-warn', label: _('Fair') },
  poor: { icon: faWifiWeak, iconClass: 'duotone-error', label: _('Poor') },
};

/**
 * Connection summary for the connection panel: overall quality, one-minute
 * graphs of ping, loss and throughput, and every video stream shown in a
 * tile with its health. A stream row opens its stream stats.
 */
@Component({
  selector: 'andor-connection-overview',
  imports: [TranslatePipe, FaDuotoneIconComponent, FaIconComponent, StatsGraph],
  templateUrl: './connection-overview.html',
  styleUrl: './connection-overview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectionOverview {
  private connection = inject(ConnectionStatsService);
  private streamHealth = inject(StreamHealthService);
  private userStore = inject(UserStore);
  private streamStatsDialog = inject(StreamStatsDialogService);

  protected readonly faChevronRight = faChevronRight;
  protected readonly rtt = this.connection.rtt;
  protected readonly loss = this.connection.loss;
  protected readonly history = this.connection.history;
  protected readonly packetsPerSec = this.connection.packetsPerSec;
  protected readonly problemCount = this.streamHealth.problemCount;
  protected readonly quality = computed(() => QUALITY[this.connection.quality()]);

  protected readonly rttTone = computed(() =>
    this.rtt() >= RTT_POOR_MS ? 'error' : this.rtt() >= RTT_FAIR_MS ? 'warn' : 'ok'
  );
  protected readonly lossTone = computed(() =>
    this.loss() > LOSS_POOR_PCT ? 'error' : this.loss() > LOSS_FAIR_PCT ? 'warn' : 'ok'
  );

  protected readonly streams = computed(() =>
    this.streamHealth.streams().map(({ userId, kind, health }) => {
      const user = this.userStore.get(userId);
      const problem = health && (health.severity === 'warn' || health.severity === 'error');
      const details = [
        health?.fps != null ? `${health.fps} fps` : null,
        health?.width && health.height ? `${health.width}×${health.height}` : null,
      ].filter(Boolean);
      return {
        key: `${userId}:${kind}`,
        userId,
        kind,
        isSelf: !!user?.isSelf,
        name: user?.data.name ?? '',
        severity: health?.severity ?? 'pending',
        // Healthy streams show their numbers, others what is wrong.
        state: problem || !details.length ? healthStateLabel(health?.state) : null,
        details: details.join(' · '),
      };
    })
  );

  protected openStats(userId: string, kind: StreamKind) {
    this.streamStatsDialog.open(userId, kind);
  }
}
